import { equals } from "ramda";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { RxDatabase } from "rxdb";
import { GENERAL_RECENCY_WINDOW_MS } from "./constants";
import { initDatabase, type RxDBCollections } from "./db";
import {
  FHIRResourceTypes,
  type SupportedClinicalResourceTypes,
} from "./db/collections/common";
import {
  ClinicalResourceSubmissionStatus,
  type PatientClinicalResource,
  RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME,
} from "./db/collections/patient-clinical-resource";
import { diseaseRecommender } from "./disease-recommender";
import {
  accumulateClinicalFindings,
  type ClinicalFindingsAccumulator,
  constructResourceId,
  DiagnosisGuidance,
  DiseaseRecommendation,
  getRemainingRecommendations,
  snomedDiseaseToDiseaseRecommendation,
} from "./mappers";
import {
  checkGuidelinesDeviation,
  DrugGuidanceDeviation,
} from "./medication-guidelines-checker/guidelines-checker";
import { useHydrateDatabase } from "./use-hydrate-database";

interface CDSService {
  patientClinicalResources: {
    add: (
      externalCodingSystem: keyof PatientClinicalResource["fhirResource"]["externalCoding"],
      resource: Omit<
        PatientClinicalResource,
        "id" | "patient" | "submissionStatus"
      >,
    ) => void;
    remove: (uri: string) => void;
    hasRecent: (
      clinicalResourceType: keyof SupportedClinicalResourceTypes,
    ) => boolean;
    submit: () => void;
    clearStaged: () => Promise<void>;
    addExternalResources: (resources: PatientClinicalResource[]) => void;
    isDbReady: boolean;
  };
  recommendations: Array<DiseaseRecommendation>;
  checkMedicationGuidanceDeviation: (vmpId: string) => DrugGuidanceDeviation;
  checkDiagnosisSuggestion: () => DiagnosisGuidance;
}

const checkDiagnosisSuggestion = (
  extractedFindings: ClinicalFindingsAccumulator,
): DiagnosisGuidance => {
  const hasPositiveMalaria =
    extractedFindings.positiveMalarialResults.length > 0;

  const hasMalariaDiagnosis = extractedFindings.malariaDiagnoses.length > 0;

  if (hasPositiveMalaria && !hasMalariaDiagnosis) {
    return {
      icd11Code: "1F4Z", // Malaria, confirmed
      displayName: "Malaria, confirmed",
    };
  }

  return null;
};

const useCreateCDSService = (patientId: string): CDSService => {
  const [db, setDb] = useState<RxDatabase<RxDBCollections> | null>(null);
  const [recommendations, setRecommendations] = useState<
    Array<DiseaseRecommendation>
  >([]);

  const initialising = useRef(false);
  const [recentResources, setRecentResources] = useState<
    PatientClinicalResource[]
  >([]);

  useHydrateDatabase({
    db,
    patientId,
  });

  const extractedFindings = useMemo(() => {
    return recentResources.reduce(accumulateClinicalFindings, {
      bacterialDiagnoses: [],
      malariaDiagnoses: [],
      positiveMalarialResults: [],
      negativeMalarialResults: [],
      findings: [],
      investigationCatalogueLoincCodes: [],
    });
  }, [recentResources]);

  useEffect(() => {
    const collection = db?.[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME];
    if (!collection || !patientId) return;

    const query = collection.find({
      selector: {
        patient: { id: patientId },
        "meta.updatedAt": {
          $gt: new Date(Date.now() - GENERAL_RECENCY_WINDOW_MS).toISOString(),
        },
      },
    });

    const subscription = query.$.subscribe((results) => {
      if (!equals(results, recentResources)) {
        setRecentResources(results);
      }
    });

    return () => subscription.unsubscribe();
  }, [db, patientId, recentResources]);

  useEffect(() => {
    if (!db || !patientId) return;

    const rawRecommendations = diseaseRecommender
      .recommend(extractedFindings.findings)
      .map(
        (recommendation) =>
          snomedDiseaseToDiseaseRecommendation[recommendation],
      );

    setRecommendations(
      getRemainingRecommendations({
        recommendations: rawRecommendations,
        investigationCatalogueLoincCodes:
          extractedFindings.investigationCatalogueLoincCodes,
      }),
    );
  }, [extractedFindings, db, patientId]);

  useEffect(() => {
    if (db || initialising.current) return;
    initialising.current = true;
    initDatabase()
      .then((database) => {
        setDb(database);
      })
      .catch((error) => {
        console.error("Error initializing CDS service:", error);
      })
      .finally(() => {
        initialising.current = false;
      });
  }, [db]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: these will be defined if db is present
  const service = useMemo(() => {
    return {
      add: (
        externalCodingSystem: keyof PatientClinicalResource["fhirResource"]["externalCoding"],
        resource: Omit<
          PatientClinicalResource,
          "id" | "patient" | "submissionStatus"
        >,
      ) => {
        if (!db || !db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]) {
          console.warn("Database not initialized yet. Cannot add resource.");
          return;
        }
        const uri =
          resource.fhirResource.externalCoding[externalCodingSystem]?.uri;

        if (!uri) {
          console.warn("No URI provided for resource.");
          return;
        }

        const compositeKey = constructResourceId(
          uri,
          patientId,
          ClinicalResourceSubmissionStatus.Staged,
        );

        db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME].upsert({
          id: compositeKey,
          fhirResource: {
            resourceType: resource.fhirResource.resourceType,
            diagnosticReport: resource.fhirResource.diagnosticReport
              ? {
                  result: resource.fhirResource.diagnosticReport.result,
                }
              : null,
            externalCoding: {
              icd10: resource.fhirResource.externalCoding.icd10 ?? null,
              icd11: resource.fhirResource.externalCoding.icd11 ?? null,
              loinc: resource.fhirResource.externalCoding.loinc ?? null,
              snomed: resource.fhirResource.externalCoding.snomed ?? null,
              elephantInternal:
                resource.fhirResource.externalCoding.elephantInternal ?? null,
            },
          },
          patient: { id: patientId },
          submissionStatus: ClinicalResourceSubmissionStatus.Staged,
          meta: resource.meta,
        });
      },
      remove: (uri: string) => {
        if (!db || !db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]) {
          console.warn("Database not initialized yet. Cannot remove resource.");
          return;
        }

        const resourceId = constructResourceId(
          uri,
          patientId,
          ClinicalResourceSubmissionStatus.Staged,
        );
        db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]
          .find({
            selector: {
              id: resourceId,
              submissionStatus: ClinicalResourceSubmissionStatus.Staged,
            },
          })
          .exec()
          .then((docs) => {
            return Promise.all(docs.map((doc) => doc.remove()));
          });
      },

      hasRecent: (
        clinicalResourceType: keyof SupportedClinicalResourceTypes,
      ): boolean => {
        return recentResources.some(
          (resource) =>
            resource.fhirResource.resourceType.uri ===
            FHIRResourceTypes[clinicalResourceType].uri,
        );
      },
      submit: async () => {
        if (!db || !db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]) {
          console.warn(
            "Database not initialized yet. Cannot submit resources.",
          );
          return;
        }

        const docs = await db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]
          .find({
            selector: {
              patient: { id: patientId },
              submissionStatus: ClinicalResourceSubmissionStatus.Staged,
            },
          })
          .exec();

        if (docs.length === 0) {
          return;
        }

        const newDocs = docs.map((doc) => {
          const jsonDoc = doc.toJSON();
          return {
            ...jsonDoc,
            fhirResource: {
              ...jsonDoc.fhirResource,
              diagnosticReport: jsonDoc.fhirResource.diagnosticReport
                ? {
                    result: [...jsonDoc.fhirResource.diagnosticReport.result],
                  }
                : null,
            },
            id: jsonDoc.id.replace(
              ClinicalResourceSubmissionStatus.Staged,
              ClinicalResourceSubmissionStatus.Submitted,
            ),
            submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
          };
        });

        await db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME].bulkUpsert(
          newDocs,
        );

        await db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]
          .find({
            selector: {
              patient: { id: patientId },
              submissionStatus: ClinicalResourceSubmissionStatus.Staged,
            },
          })
          .remove();
      },
      clearStaged: async () => {
        if (!db || !db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]) {
          console.warn("Database not initialized yet. Cannot clear resources.");
          return;
        }
        await db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]
          .find({
            selector: {
              submissionStatus: ClinicalResourceSubmissionStatus.Staged,
            },
          })
          .remove();
      },
      isDbReady: !!db,
      addExternalResources: (resources: PatientClinicalResource[]) => {
        if (!db || !db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]) {
          console.warn("Database not initialized yet. Cannot add resources.");
          return;
        }
        db[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME].bulkUpsert(
          resources,
        );
      },
    };
  }, [db, patientId]);

  return {
    patientClinicalResources: service,
    recommendations,
    checkMedicationGuidanceDeviation: (vmpId: string) =>
      checkGuidelinesDeviation({
        vmpId,
        recentRelevantResources: extractedFindings,
      }),
    checkDiagnosisSuggestion: () => checkDiagnosisSuggestion(extractedFindings),
  };
};

const CDSServiceContext = createContext<CDSService | null>(null);

const FlagOnProvider: FCwC<{
  patientId?: string;
}> = ({ patientId, children }) => {
  const service = useCreateCDSService(patientId);

  useEffect(() => {
    if (service.patientClinicalResources.isDbReady) {
      service.patientClinicalResources.clearStaged();
    }
  }, [
    service.patientClinicalResources.isDbReady,
    service.patientClinicalResources.clearStaged,
  ]);

  return (
    <CDSServiceContext.Provider value={service}>
      {children}
    </CDSServiceContext.Provider>
  );
};

export const ClinicalDecisionSupportProvider: FCwC<{
  patientId?: string;
}> = ({ patientId, children }) => {
  return <FlagOnProvider patientId={patientId}>{children}</FlagOnProvider>;
};

export const useClinicalDecisionSupportService = () => {
  const context = useContext(CDSServiceContext);
  if (!context) {
    throw new Error(
      "useClinicalDecisionSupportService must be used within a ClinicalDecisionSupportProvider.",
    );
  }
  return context;
};
